import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getOrder } from "../services/orderService";
import { friendlyError } from "../api/api";
import ErrorState from "../components/ui/ErrorState";
import { formatBusinessDateTime } from "../utils/businessDate";
import { formatMoney } from "../utils/money";

export default function OrderDetail() {
  const { id } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ok | error | forbidden | not_found
  const [order, setOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const load = () => {
    setStatus("loading");
    getOrder(id)
      .then((data) => {
        setOrder(data);
        setStatus("ok");
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 403) return setStatus("forbidden");
        if (code === 404) return setStatus("not_found");
        setErrorMessage(friendlyError(err, "Unable to load this order."));
        setStatus("error");
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (status === "loading") {
    return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-sm text-neutral-500">Loading order...</div>;
  }
  if (status === "forbidden") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-sm font-medium text-neutral-700">You don&apos;t have access to this order.</p>
        <Link to="/orders" className="mt-3 inline-block text-sm text-neutral-600 underline">
          Back to Order History
        </Link>
      </div>
    );
  }
  if (status === "not_found") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-sm font-medium text-neutral-700">Order not found.</p>
        <Link to="/orders" className="mt-3 inline-block text-sm text-neutral-600 underline">
          Back to Order History
        </Link>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <ErrorState message={errorMessage} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Link to="/orders" className="text-sm text-neutral-500 hover:text-neutral-900">
        ← Back to Order History
      </Link>

      <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-neutral-900">{order.order_code}</h1>
          <span className="text-xs rounded-full px-2 py-0.5 bg-neutral-100 text-neutral-600 capitalize">
            {order.status}
          </span>
        </div>
        <p className="text-sm text-neutral-500 mt-1">
          {formatBusinessDateTime(order.created_at)}
        </p>

        <div className="mt-5">
          <h2 className="text-sm font-medium text-neutral-900 mb-2">Items</h2>
          <div className="space-y-2">
            {order.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-neutral-700">
                  {item.product_name} × {item.quantity}
                  {item.sold_unit ? ` ${item.sold_unit}` : ""}
                </span>
                <span className="text-neutral-900">{formatMoney(item.total || 0)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-neutral-100 space-y-1 text-sm">
          <div className="flex justify-between text-neutral-500">
            <span>Subtotal</span>
            <span>{formatMoney(order.subtotal || 0)}</span>
          </div>
          {Number(order.discount || 0) > 0 && (
            <div className="flex justify-between text-neutral-500">
              <span>Discount</span>
              <span>-{formatMoney(order.discount)}</span>
            </div>
          )}
          {Number(order.tax || 0) > 0 && (
            <div className="flex justify-between text-neutral-500">
              <span>Tax</span>
              <span>{formatMoney(order.tax)}</span>
            </div>
          )}
          <div className="flex justify-between font-medium text-neutral-900">
            <span>Total</span>
            <span>{formatMoney(order.total || 0)}</span>
          </div>
        </div>

        {order.shipping_location && (
          <p className="mt-4 text-sm text-neutral-500">Delivery: {order.shipping_location}</p>
        )}
      </div>
    </div>
  );
}
